import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import { Portal } from "@/components/Portal";
import { buildToken, renderQRSticker } from "@/lib/qrLabel";
import {
  isBluetoothSupported, isPrinterConnected, connectPrinter, reconnectPrinter, printCanvas,
} from "@/lib/btPrinter";
import { QrCode, Printer, Bluetooth, X, Loader2, Check } from "lucide-react";

const QR_DIMS_DEFAULT = { widthMm: 80, heightMm: 60, dpi: 203, rotate: false };

// Build the printable QR items for a tenant, grouped by site.
function buildGroups(data) {
  if (!data) return [];
  const bySite = {};
  const ensure = (sid, sname) => {
    if (!bySite[sid]) bySite[sid] = { siteId: sid, siteName: sname || "—", items: [] };
    return bySite[sid];
  };
  (data.sites || []).forEach((s) => ensure(s.id, s.name));
  (data.facilities || []).forEach((f) => {
    const g = ensure(f.site_id, f.site_name);
    if (f.daily_hygiene_enabled) {
      g.items.push({
        key: `fac_hyg:${f.id}`, kind: "fac_hyg", id: f.id, title: f.name, tag: "Hygiene",
        lines: [f.name || "", `Daily Hygiene · ${f.site_name || ""}`, "Vanguard Beacon"],
      });
    }
    if (f.daily_cleanliness_enabled) {
      g.items.push({
        key: `fac_clean:${f.id}`, kind: "fac_clean", id: f.id, title: f.name, tag: "Cleanliness",
        lines: [f.name || "", `Daily Cleanliness · ${f.site_name || ""}`, "Vanguard Beacon"],
      });
    }
  });
  (data.equipment || []).forEach((e) => {
    const g = ensure(e.site_id, e.site_name);
    g.items.push({
      key: `eq:${e.id}`, kind: "eq", id: e.id, title: e.name, tag: "Equipment",
      lines: [
        (e.name || "Equipment").toUpperCase(),
        [e.site_name, e.facility_name].filter(Boolean).join("  ·  ") || "—",
        e.serial_number ? `S/N: ${e.serial_number}` : null,
      ].filter(Boolean),
    });
  });
  return Object.values(bySite).filter((g) => g.items.length);
}

export default function QRStickerModal({ tenant, onClose }) {
  const { notify } = useDialog();
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [connected, setConnected] = useState(isPrinterConnected());
  const canvases = useRef({});   // key -> canvas element
  const qtys = useRef({});       // key -> quantity

  useEffect(() => {
    (async () => {
      try {
        const [items, config] = await Promise.all([
          api.get(`/admin/tenants/${tenant.id}/qr-items`),
          api.get(`/admin/food-prep-config`),
        ]);
        setData(items.data);
        setCfg(config.data);
      } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Could not load QR items."); }
    })();
  }, [tenant.id]);

  const qrDims = useMemo(() => ({
    widthMm: (cfg && cfg.qr_label_width_mm) || QR_DIMS_DEFAULT.widthMm,
    heightMm: (cfg && cfg.qr_label_height_mm) || QR_DIMS_DEFAULT.heightMm,
    dpi: 203,
    rotate: !!(cfg && cfg.qr_label_rotate),
  }), [cfg]);

  const qrCfg = useMemo(() => ({
    ...(cfg || {}),
    label_width_mm: qrDims.widthMm,
    label_height_mm: qrDims.heightMm,
    label_dpi: qrDims.dpi,
    printer_command: (cfg && cfg.printer_command) || "tspl",
    printer_gap_mm: (cfg && cfg.qr_label_gap_mm != null) ? cfg.qr_label_gap_mm
      : ((cfg && cfg.printer_gap_mm) != null ? cfg.printer_gap_mm : 2),
  }), [cfg, qrDims]);

  const groups = useMemo(() => buildGroups(data), [data]);
  const totalItems = groups.reduce((n, g) => n + g.items.length, 0);

  const ensureConnected = async () => {
    if (!isBluetoothSupported()) throw new Error("Direct printing needs Chrome on an Android device.");
    if (!isPrinterConnected()) {
      const ok = await reconnectPrinter(qrCfg);
      if (!ok) await connectPrinter(qrCfg);
    }
    setConnected(true);
  };

  const connect = async () => {
    setBusy(true);
    try { await ensureConnected(); await notify({ title: "Printer connected", variant: "ok" }); }
    catch (e) { await notify({ title: "Couldn't connect", message: e.message, variant: "err" }); }
    finally { setBusy(false); }
  };

  const qtyOf = (key) => Math.max(1, parseInt(qtys.current[key] || 1, 10) || 1);

  // Get a print-ready canvas for an item, rendering on demand if the preview
  // canvas isn't ready yet (mobile renders many canvases slowly).
  const getCanvas = async (it) => {
    let cv = canvases.current[it.key];
    if (!cv) {
      cv = document.createElement("canvas");
      canvases.current[it.key] = cv;
    }
    if (!cv.width) {
      await renderQRSticker(cv, buildToken(it.kind, it.id), it.lines, qrDims);
    }
    return cv;
  };

  const printItems = async (items, label) => {
    setBusy(true);
    try {
      await ensureConnected();
      let count = 0;
      for (const it of items) {
        const cv = await getCanvas(it);
        const q = qtyOf(it.key);
        await printCanvas(cv, qrCfg, q);
        count += q;
      }
      await notify({ title: `Sent ${count} sticker${count === 1 ? "" : "s"} to printer`, message: label, variant: "ok" });
    } catch (e) {
      await notify({ title: "Print failed", message: e.message || "Could not print.", variant: "err" });
    } finally { setBusy(false); }
  };

  const allItems = groups.flatMap((g) => g.items);

  return (
    <Portal>
      <div className="vb-modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="vb-modal" style={{ maxWidth: 760, maxHeight: "90vh", overflowY: "auto" }} data-testid="qr-sticker-modal">
          <div className="flex items-center justify-between mb-1">
            <h2 className="m-0 flex items-center gap-2"><QrCode size={18} /> QR stickers · {tenant.company_name}</h2>
            <button className="vb-btn ghost" onClick={onClose} data-testid="qr-sticker-close"><X size={16} /></button>
          </div>
          <p className="text-xs opacity-70 mb-3">
            Prints at <strong>{qrDims.widthMm} × {qrDims.heightMm} mm</strong> on the thermal printer (TSPL, 203 dpi)
            {qrDims.rotate ? " · rotated 90°" : ""}. Adjust the size in <strong>Brand Settings → QR sticker label size</strong>.
            Load the matching QR roll, connect the printer, set quantities, then print. Stickers scan only in the Vanguard Beacon app.
          </p>

          <div className="vb-card p-3 mb-3 flex items-center justify-between gap-2 flex-wrap" style={{ background: "var(--vb-bg)" }}>
            <span className="text-sm flex items-center gap-2">
              {connected
                ? <><Check size={15} style={{ color: "#16a34a" }} /> Printer connected</>
                : <><Bluetooth size={15} /> Printer not connected</>}
            </span>
            {!connected && (
              <button className="vb-btn sm" disabled={busy} onClick={connect} data-testid="qr-sticker-connect">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Bluetooth size={14} />} Connect printer
              </button>
            )}
          </div>

          {err && <div className="vb-pill err mb-3" data-testid="qr-sticker-error">{err}</div>}
          {!data && !err && <p className="text-sm opacity-60">Loading QR items…</p>}
          {data && totalItems === 0 && (
            <p className="text-sm opacity-70">This customer has no facilities (with Hygiene/Cleanliness enabled) or equipment yet.</p>
          )}

          {groups.map((g) => (
            <div key={g.siteId} className="mb-4" data-testid={`qr-site-${g.siteId}`}>
              <div className="flex items-center justify-between gap-2 mb-2 pb-1" style={{ borderBottom: "1px solid var(--vb-border, #e5d9cc)" }}>
                <h3 className="vb-heading text-base m-0">{g.siteName} <span className="text-xs opacity-60">({g.items.length})</span></h3>
                <button className="vb-btn ghost sm" disabled={busy} onClick={() => printItems(g.items, g.siteName)} data-testid={`qr-print-site-${g.siteId}`}>
                  <Printer size={13} /> Print all for site
                </button>
              </div>
              <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))" }}>
                {g.items.map((it) => (
                  <StickerCard
                    key={it.key} item={it} busy={busy} dims={qrDims}
                    register={(cv) => { if (cv) canvases.current[it.key] = cv; }}
                    onQty={(v) => { qtys.current[it.key] = v; }}
                    onPrint={() => printItems([it], it.title)}
                  />
                ))}
              </div>
            </div>
          ))}

          {totalItems > 0 && (
            <div className="flex justify-end gap-2 mt-2 pt-3" style={{ borderTop: "1px solid var(--vb-border, #e5d9cc)" }}>
              <button className="vb-btn ghost" onClick={onClose}>Close</button>
              <button className="vb-btn" disabled={busy} onClick={() => printItems(allItems, "All sites")} data-testid="qr-print-all">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Printer size={15} />} Print all ({totalItems})
              </button>
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}

function StickerCard({ item, register, onQty, onPrint, busy, dims }) {
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ref.current) return;
      await renderQRSticker(ref.current, buildToken(item.kind, item.id), item.lines, dims);
      if (cancelled) return;
      register(ref.current);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.key, dims]);

  return (
    <div className="vb-card p-3" data-testid={`qr-card-${item.key}`}>
      <div className="flex items-center justify-between gap-1 mb-1">
        <strong className="text-sm truncate" title={item.title}>{item.title}</strong>
        <span className="text-[10px] uppercase opacity-60 shrink-0">{item.tag}</span>
      </div>
      <div className="flex justify-center mb-2" style={{ background: "#f5f1ea", padding: 8, borderRadius: 8 }}>
        <canvas ref={ref} style={{ width: "100%", maxWidth: 150, height: "auto", background: "#fff" }} data-testid={`qr-card-canvas-${item.key}`} />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs opacity-70">Qty</label>
        <input type="number" min="1" defaultValue={1} className="vb-input" style={{ width: 60, padding: "4px 8px" }}
          onChange={(e) => onQty(e.target.value)} data-testid={`qr-qty-${item.key}`} />
        <button className="vb-btn sm ml-auto" disabled={busy} onClick={onPrint} data-testid={`qr-print-${item.key}`}>
          <Printer size={13} /> Print
        </button>
      </div>
    </div>
  );
}
